import { CommonModule } from '@angular/common';
import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { PacDetailComponent } from '../../components/pac-detail/pac-detail.component';
import { PaymentCollectionDetailsComponent } from '../../components/payment-collection-details/payment-collection-details.component';
import { PaymentMethodComponent } from '../../components/payment-method/payment-method.component';
import { Router, ActivatedRoute } from '@angular/router';
import { AdditionalPACData, PaidPaymentCollection } from '../../models/additional-pac-data.model';
import { PacService } from '../../shared/services/pac.service';
import {catchError, EMPTY, Subscription, tap} from 'rxjs';
import { finalize } from 'rxjs/operators';
import { VirtualKeyboardComponent } from '../../components/virtual-keyboard/virtual-keyboard.component';
import { CustomModalComponent } from '../../components/custom-modal/custom-modal.component';
import { CourtDepositService } from '../../shared/services/court-deposit.service';
import { AppConstants } from '../../shared/app-constants';
import {SessionEventService} from '../../shared/services/session-event-service';
import { SessionResponse } from '../../models/session-response.model';
import {PaymentErrorService} from '../../shared/services/payment-error-service';
import {SuccessBannerComponent} from '../../components/success-banner/success-banner.component';
import {ErrorBannerComponent} from '../../components/error-banner/error-banner.component';
import {PaymentBlockService} from '../../services/payment-block.service';

@Component({
  selector: 'app-court-deposit-details',
  standalone: true,
  imports: [
    CommonModule,
    PacDetailComponent,
    PaymentCollectionDetailsComponent,
    PaymentMethodComponent,
    VirtualKeyboardComponent,
    CustomModalComponent,
    SuccessBannerComponent,
    ErrorBannerComponent
  ],
  templateUrl: './court-deposit-details.component.html',
  styleUrl: './court-deposit-details.component.scss',
})
export class CourtDepositDetailsComponent implements OnInit, OnDestroy {
  pacDetails!: AdditionalPACData;
  anyPaymentProcessed: boolean = false;
  @ViewChild('abortModalRef') abortModalRef!: CustomModalComponent;
  creditPaymentDetails!: PaidPaymentCollection;
  isPayNowTransactionMissing: boolean = false;
  payNowAttempted: number = 0;
  private sessionEventSub!: Subscription;
  private blockResolvedSub!: Subscription;
  hasPaymentModes: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  private hasHandledSessionEvent: boolean = false;


  constructor(
    private readonly router: Router,
    private readonly pacService: PacService,
    private readonly route: ActivatedRoute,
    private readonly courtDepositService: CourtDepositService,
    private readonly sessionEventService: SessionEventService,
    private readonly paymentErrorService: PaymentErrorService,
    private readonly paymentBlockService: PaymentBlockService
) {}

  ngOnInit(): void {
    this.blockResolvedSub = this.paymentBlockService.blockResolved$.subscribe(data => {
      if (data?.pacNumber && data.pacNumber === this.pacDetails?.pacNumber) {
        this.assignPacDetails(data);
      }
    });
    this.loadData();
    // Drop any event left over from an earlier page/PAC - the BehaviorSubject would otherwise replay it for this new PAC
    this.sessionEventService.clearSessionEvent();
    this.sessionEventSub = this.sessionEventService.getSessionEvent()
      .subscribe((event) => {
        console.log("getSessionEvent subscriber: ", event, this.hasHandledSessionEvent)
        const pacNumber = this.pacDetails?.pacNumber;
        if (!event || this.hasHandledSessionEvent || !pacNumber) return;
        //Status api is being called multiple times in case of qr open, to prevent that duplicate handling we used this hasHandledSessionEvent flag
        this.hasHandledSessionEvent = true;
        // Deposit already created via /deposit-payment - don't expire/abort it, just show the result
        if (this.sessionEventService.isDepositCreated(pacNumber)) {
          this.sessionEventService.clearSessionEvent();
          this.redirectToSuccessPaymentPage();
          return;
        }
        console.log("call sendSessionStatus for PAC ", pacNumber)
            this.pacService.sendSessionStatus(pacNumber, event).subscribe({
                next: (response: SessionResponse) => {
                  this.sessionEventService.clearSessionEvent();
                  this.routeAfterSessionEvent(event, response.paymentDetected);
                },
                error: () => {
                  this.sessionEventService.clearSessionEvent();
                  this.routeAfterSessionEvent(event, false);
                }
            });
      });
  }

  private routeAfterSessionEvent(event: string, paymentDetected?: boolean): void {
    if (event === 'ABORTED' && paymentDetected === true) {
      this.redirectToSuccessPaymentPage();
    } else {
      this.router.navigateByUrl('/');
    }
  }

  ngOnDestroy(): void {
    if (this.sessionEventSub) {
      this.sessionEventSub.unsubscribe();
    }
    this.blockResolvedSub?.unsubscribe();
    this.hasHandledSessionEvent = false;
  }

  loadData(): void {
    const pacData = history.state?.depositInfo as AdditionalPACData | undefined;

    if (pacData) {
      this.assignPacDetails(pacData);
      this.fetchCourtDepositPacDetails(this.pacDetails.pacNumber);
    } else {
      // Fallback: read pacNumber from query params
      this.route.queryParams.subscribe(params => {
        const pacReference = params['pacReference'];
        const status = params['status'];
        const amount = params['amount'];
        const transactionId = params['transactionId'];
        const responseCode = params['reasonCode'];
        this.paymentErrorService.getMessage(responseCode, status).subscribe(message => {
          if (status === 'failure') {
            this.errorMessage = message;
          } else {
            this.successMessage = message;
          }
        });
        if (pacReference) {
          this.creditPaymentDetails = {
            paymentMode: 'CREDIT_CARD',
            bankReference: '',
            paymentAmount: Number.isNaN(Number(amount)) ? 0 : Number(amount), //unexpected constant nullishness ont the left hand side of a ?? expression
            bankCode: transactionId,
            transactionId,
            status: params['status'],
            message: AppConstants.getDisplayMessage(params['messageCode']),
            pacNumber: pacReference
          };
          this.fetchCourtDepositPacDetails(pacReference);
        }
      });
    }
  }

  assignPacDetails(pacData: AdditionalPACData): void {
    this.pacDetails = pacData;
    this.pacDetails.paidLineItems ??= [];
    if (pacData.balancePayable == pacData.totalAmountPayable && this.pacDetails.paidLineItems.length == 0) {
      this.anyPaymentProcessed = false;
    } else {
      this.anyPaymentProcessed = true;
    }
  }

  onUpdatePacDetails(updatedPacData: AdditionalPACData): void {
    this.assignPacDetails(updatedPacData);
  }


  onClickHomeButton(): void {
    if (!this.pacDetails?.pacNumber) {
      this.router.navigate(['']);
      return;
    }
    this.pacService.fetchAdditionalInfo(this.pacDetails.pacNumber).subscribe({
      next: (additionalPaymentInfo: AdditionalPACData) => {
        this.assignPacDetails(additionalPaymentInfo);
        if (this.hasPaymentModes) {
          this.abortModalRef.showModal();
        } else {
          this.redirectToHomePage();
        }
      },
      error: () => { // NOSONAR
        // intentionally ignored or handled globally
      }
    });
  }

  fetchCourtDepositPacDetails(pacNumber: any): void {
    if (!pacNumber) return;
    this.pacService.fetchAdditionalInfo(pacNumber).pipe(
      tap(response => {
        this.assignPacDetails(response);
        let isPaymentCompleted = Math.round(this.pacDetails.balancePayable * 100) <= 0;
        if (isPaymentCompleted) {
          this.redirectToSuccessPaymentPage();
        }
      }),
      catchError(error => {
        // Optionally: this.toastService.error('Could not load PAC details.');
        return EMPTY; // prevent further subscription
      }),
      finalize(() => {
      })
    ).subscribe();
  }

  handleAbortModalClose(confirmed: boolean) {
    this.abortModalRef.hideModal();
    if (confirmed) {
      if (this.anyPaymentProcessed) {
        this.sessionEventService.setSessionEvent('ABORTED');
      } else {
        this.redirectToHomePage();
      }
    }
  }



  redirectToSuccessPaymentPage() {
    this.payNowAttemptedCheck();
    this.router.navigate(['payment-success'], {
      state: {pacDetails: this.pacDetails, isPayNowTransactionMissing: this.isPayNowTransactionMissing, payNowAttempted: this.payNowAttempted}
    });
  }

  redirectToHomePage() {
    this.sessionEventService.setSessionEvent('ABORTED');
    this.router.navigate(['']);
  }

  payNowAttemptedCheck() {
    const attemptKey = 'PAY_NOW_ATTEMPTED';
    this.payNowAttempted = Number(localStorage.getItem(attemptKey)) || 0;
    const payNowTransactionsFoundCount = this.pacDetails.payNowCompletedCount;
    if (payNowTransactionsFoundCount != undefined && payNowTransactionsFoundCount < this.payNowAttempted) {
      this.isPayNowTransactionMissing = true;
    }
  }

  onValidPaymentMethods(available: boolean) {
    this.hasPaymentModes = available;
  }

  @HostListener('window:click')
  @HostListener('window:keydown')
  handleClearMessageTrigger(): void {
    this.paymentErrorService.clearMessagesWithDelay(() => {
      this.errorMessage = '';
      this.successMessage = '';
    }, AppConstants.MESSAGE_CLEAR_DELAY_MS);
  }
}
